import {
  Camera,
  Clock,
  Eye,
  EyeOff,
  KeyRound,
  MapPin,
  MessageSquare,
  Play,
  TriangleAlert,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';

import { EmployeePage } from '@/components/employee/EmployeePage';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { useCompleteMyJob, useMyJob, useStartMyJob } from '@/hooks/employee-portal';
import { JOB_STATUS_TONES } from '@/hooks/jobs';
import { ApiError } from '@/lib/api';
import { formatDay, formatDuration, formatInstant, formatWallTime } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import type { EmployeeJobDetail, EmployeeJobTaskView } from '@shared/schemas/employee-portal';
import { PhotoCaptureSheet } from './jobs/PhotoCaptureSheet';
import { TaskChecklist } from './jobs/TaskChecklist';
import { UploadQueueBanner } from './jobs/UploadQueueBanner';

/*
 * /employee/jobs/:id — ROADMAP.md §6.2, §6.3 and §6.4.
 *
 * **Assigned jobs only, and the server decides that**, not this page: every read
 * behind it is scoped by an `EXISTS` against `job_assignment` in the same query,
 * and a job somebody else is on answers 404 — the same answer an invented id
 * gets (business-rules.md §2).
 *
 * The order of the page is the order of the work. A cleaner arrives, so the
 * address and the lockbox code come first; then Start Job; then the checklist,
 * which is where the next two hours go; then the photos already taken.
 *
 * **Start Job is not clock-in.** It sets `started_at` and moves the job to In
 * Progress. Clocking in starts a shift and one clock-in covers several jobs —
 * the two live on different screens and write different tables, which is the
 * distinction the Airo roadmap lost (FINDINGS.md §11.2).
 */

export function EmployeeJobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const job = useMyJob(id);

  const [captureFor, setCaptureFor] = useState<EmployeeJobTaskView | null>(null);
  const [captureOpen, setCaptureOpen] = useState(false);

  const detail = job.data;

  if (job.error) {
    const notFound = job.error instanceof ApiError && job.error.status === 404;

    return (
      <EmployeePage title="Job" back={{ to: '/employee/jobs', label: 'My Jobs' }}>
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          {/*
            A 404 here means the job is not one of theirs — which is also what an
            id that never existed gets, deliberately. Saying "not on your list"
            is honest about both without confirming anything.
          */}
          <ErrorState
            title={notFound ? 'That job is not on your list' : 'We could not load this job'}
            hint={
              notFound
                ? 'You can only open jobs you are assigned to. If you should be on this one, ring the office.'
                : 'Something went wrong on our side. Try again, and ring the office if it keeps happening.'
            }
            {...(notFound
              ? {}
              : {
                  onRetry: () => {
                    void job.refetch();
                  },
                })}
          />
        </div>
      </EmployeePage>
    );
  }

  if (job.isPending || !detail) {
    return (
      <EmployeePage title="Job" back={{ to: '/employee/jobs', label: 'My Jobs' }}>
        <div aria-busy="true" className="space-y-4">
          <span className="sr-only">Loading this job</span>
          {[0, 1, 2].map((key) => (
            <div
              key={key}
              aria-hidden="true"
              className="h-40 animate-pulse rounded-lg bg-ink-100"
            />
          ))}
        </div>
      </EmployeePage>
    );
  }

  return (
    <EmployeePage
      title={detail.clientName}
      description={`${detail.jobNumber} · ${detail.serviceTypeName}`}
      back={{ to: '/employee/jobs', label: 'My Jobs' }}
      actions={<StatusBadge label={detail.statusLabel} tone={JOB_STATUS_TONES[detail.status]} />}
    >
      <div className="space-y-4">
        {/* Above everything, because a photo that has not landed is the one
            thing on this screen that can still be lost. */}
        <UploadQueueBanner jobId={detail.id} />
        <LifecycleActions detail={detail} />
        <WhereAndWhen detail={detail} />
        {detail.specialInstructions === null &&
        detail.property?.specialInstructions === null ? null : (
          <Instructions detail={detail} />
        )}

        <TaskChecklist
          detail={detail}
          onAddPhoto={(task) => {
            setCaptureFor(task);
            setCaptureOpen(true);
          }}
        />

        <PhotoGallery
          detail={detail}
          onAddPhoto={() => {
            setCaptureFor(null);
            setCaptureOpen(true);
          }}
        />
      </div>

      {/*
        Mounted only while open, and keyed by the task it was opened for — so
        reopening for a different task starts with an empty caption and no
        half-chosen file. A fresh mount is a fresh `useState`; a reset effect
        is four lines that can each be forgotten.
      */}
      {captureOpen ? (
        <PhotoCaptureSheet
          key={captureFor?.id ?? 'general'}
          jobId={detail.id}
          task={captureFor}
          open
          onOpenChange={setCaptureOpen}
        />
      ) : null}
    </EmployeePage>
  );
}

/**
 * The one or two things this cleaner can do to the job right now.
 *
 * `canStart` and `canComplete` come from the server, so the screen never offers
 * a button the lifecycle is going to refuse — and `jobService` refuses anyway,
 * which is what makes this a courtesy rather than the rule.
 */
function LifecycleActions({ detail }: { detail: EmployeeJobDetail }) {
  const toast = useToast();
  const start = useStartMyJob(detail.id);
  const complete = useCompleteMyJob(detail.id);

  if (detail.status === 'cancelled') {
    return (
      <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
        <p className="text-label text-ink-900">This job was cancelled.</p>
        <p className="mt-1 text-body text-ink-700">
          {detail.cancelledReason ?? 'The office did not record a reason.'}
        </p>
      </section>
    );
  }

  if (detail.status === 'completed') {
    return (
      <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
        <p className="text-label text-status-completed-text">Finished</p>
        <p className="mt-1 text-body text-ink-700">
          {detail.completedAt === null
            ? 'Marked complete.'
            : `Marked complete ${formatInstant(detail.completedAt)}.`}
        </p>
      </section>
    );
  }

  const openRequired = detail.progress.requiredTotal - detail.progress.requiredCompleted;

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      {detail.canStart ? (
        <>
          <Button
            size="lg"
            full
            disabled={start.isPending}
            onClick={() => {
              start.mutate(undefined, {
                onError: (error) => {
                  toast.error(
                    error instanceof ApiError ? error.message : 'Could not start the job.',
                  );
                },
              });
            }}
          >
            <Play aria-hidden="true" />
            {start.isPending ? 'Starting…' : 'Start job'}
          </Button>
          {/* The distinction, said once where it matters, because the prototype
              conflates them and a cleaner reasonably assumes one does the other. */}
          <p className="mt-2 text-center text-meta text-ink-500">
            This starts the job, not your shift. Clock in from the dashboard.
          </p>
        </>
      ) : detail.status === 'in_progress' ? (
        <>
          <Button
            size="lg"
            full
            disabled={!detail.canComplete || complete.isPending}
            onClick={() => {
              complete.mutate(undefined, {
                onSuccess: () => {
                  toast.success(`${detail.jobNumber} finished.`);
                },
                onError: (error) => {
                  toast.error(
                    error instanceof ApiError ? error.message : 'Could not finish the job.',
                  );
                },
              });
            }}
          >
            {complete.isPending ? 'Finishing…' : 'Finish job'}
          </Button>
          <p className="mt-2 text-center text-meta text-ink-500">
            {detail.canComplete
              ? 'Every required task has its photo.'
              : /*
                 The button is disabled *and* says why. A disabled control with
                 no explanation is the thing people ring the office about — and
                 the server refuses this too, in the same words.
                 */
                `${String(openRequired)} task${openRequired === 1 ? '' : 's'} still need${
                  openRequired === 1 ? 's' : ''
                } a photo before you can finish.`}
          </p>
        </>
      ) : (
        <p className="text-body text-ink-700">
          {/* Pending: the office has not finished scheduling it. */}
          The office has not scheduled this one yet.
        </p>
      )}
    </section>
  );
}

function WhereAndWhen({ detail }: { detail: EmployeeJobDetail }) {
  const [codesVisible, setCodesVisible] = useState(false);
  const property = detail.property;

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="text-h3 text-ink-900">Where and when</h2>

      <dl className="mt-3 space-y-2.5 text-body">
        <div className="flex items-start gap-2">
          <dt className="sr-only">Address</dt>
          <MapPin aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
          <dd className="min-w-0 text-ink-900">
            {property?.nickname ? (
              <span className="font-medium">{property.nickname} · </span>
            ) : null}
            {detail.addressSnapshot}
          </dd>
        </div>

        <div className="flex items-start gap-2">
          <dt className="sr-only">When</dt>
          <Clock aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
          <dd className="text-ink-900">
            {formatDay(detail.scheduledDate)} at {formatWallTime(detail.scheduledTime)} ·{' '}
            {formatDuration(detail.estimatedDurationMinutes)} expected
            {detail.startedAt === null ? null : (
              <span className="block text-meta text-ink-500">
                Started {formatInstant(detail.startedAt)}
              </span>
            )}
          </dd>
        </div>

        {detail.crew.length > 1 ? (
          <div className="flex items-start gap-2">
            <dt className="sr-only">Crew</dt>
            <Users aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
            <dd className="text-ink-900">
              {detail.crew
                .map((member) => (member.isMe ? 'You' : `${member.name} (${member.roleLabel})`))
                .join(', ')}
            </dd>
          </div>
        ) : null}
      </dl>

      {property?.accessNotes ? (
        <div className="mt-3 rounded-sm border border-ink-300 bg-ink-100/40 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-label text-ink-900">
              <KeyRound aria-hidden="true" className="size-4" />
              How to get in
            </p>
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={codesVisible}
              onClick={() => {
                setCodesVisible((current) => !current);
              }}
            >
              {codesVisible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
              {codesVisible ? 'Hide' : 'Show'}
            </Button>
          </div>
          {codesVisible ? (
            <p className="mt-2 whitespace-pre-wrap text-body text-ink-900">
              {property.accessNotes}
            </p>
          ) : (
            /*
             Hidden behind a tap rather than printed on the screen. These are
             lockbox and gate codes for a customer's home, and a phone gets left
             face-up on a worktop. business-rules.md §2 gives them to an employee
             only for a job they are assigned to, which the server enforced
             before this page ever loaded.
            */
            <p className="mt-2 text-meta text-ink-500">
              Hidden until you tap Show — these are the customer&rsquo;s door codes.
            </p>
          )}
        </div>
      ) : null}

      {/*
        **Pets and allergies, and this is the first time either has reached a
        cleaner.** `pet_notes` has existed since phase 3 and only ever appeared
        on the admin's Preferences tab — the office could read about the dog and
        the person opening the door could not.

        Not hidden behind a tap, unlike the door codes above: a code is a secret
        worth one extra gesture, an allergy is something you need to have already
        read before you reach for a bottle. Shown in full, always.

        The whole card is absent when the office has recorded neither, rather
        than saying "no allergies recorded" — that would read as *checked and
        clear*, which is a claim nobody has made.
      */}
      {detail.household ? (
        <div className="mt-3 rounded-sm border border-honey-300 bg-honey-50/60 p-3">
          <p className="flex items-center gap-1.5 text-label text-bark-900">
            <TriangleAlert aria-hidden="true" className="size-4" />
            Before you start
          </p>
          {detail.household.allergyNotes ? (
            <p className="mt-2 text-body text-ink-900">
              <span className="font-medium">Allergies: </span>
              <span className="whitespace-pre-wrap">{detail.household.allergyNotes}</span>
            </p>
          ) : null}
          {detail.household.petNotes ? (
            <p className="mt-2 text-body text-ink-900">
              <span className="font-medium">Pets: </span>
              <span className="whitespace-pre-wrap">{detail.household.petNotes}</span>
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function Instructions({ detail }: { detail: EmployeeJobDetail }) {
  const standing = detail.property?.specialInstructions ?? null;

  /*
   * The two can hold the same sentence — the office copies the address's
   * standing note onto a job often enough that it is normal, not a coincidence.
   * Printing it twice under two different headings reads as two instructions
   * and makes somebody stop and work out which one wins.
   */
  const showStanding = standing !== null && standing !== detail.specialInstructions;

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="flex items-center gap-1.5 text-h3 text-ink-900">
        <MessageSquare aria-hidden="true" className="size-4 text-ink-500" />
        Instructions
      </h2>

      {detail.specialInstructions === null ? null : (
        <p className="mt-3 whitespace-pre-wrap text-body text-ink-900">
          {detail.specialInstructions}
        </p>
      )}

      {showStanding ? (
        <div className="mt-3">
          <p className="text-meta text-ink-500">Always at this address</p>
          <p className="mt-0.5 whitespace-pre-wrap text-body text-ink-900">{standing}</p>
        </div>
      ) : null}
    </section>
  );
}

function PhotoGallery({
  detail,
  onAddPhoto,
}: {
  detail: EmployeeJobDetail;
  onAddPhoto: () => void;
}) {
  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-h3 text-ink-900">Photos ({detail.photos.length})</h2>
        {detail.canUploadPhotos ? (
          <Button variant="outline" size="sm" onClick={onAddPhoto}>
            <Camera aria-hidden="true" />
            Add photo
          </Button>
        ) : null}
      </div>

      {detail.photos.length === 0 ? (
        <p className="mt-3 text-body text-ink-500">
          No photos yet. Every task photo you upload lands here too.
        </p>
      ) : (
        <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {detail.photos.map((photo) => (
            <li key={photo.id}>
              <img
                src={photo.url}
                alt={photo.caption ?? `${photo.kindLabel} photo`}
                loading="lazy"
                className="aspect-square w-full rounded-sm border border-ink-300 object-cover"
              />
              <p className="mt-1 text-meta text-ink-500">
                {photo.kindLabel}
                {photo.caption === null ? '' : ` · ${photo.caption}`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
